import { Box, Button, CircularProgress, Drawer, IconButton, Stack, Typography } from '@mui/material';
import { IconRefresh, IconX } from '@tabler/icons-react';
import { useQuery, useQueryClient } from 'react-query';

import AIManagersService from 'src/services/AIManagersService';
import EventService from 'src/services/EventService';

import ConversationContextSummary from './ConversationContextSummary';
import ConversationHandoffAction from './ConversationHandoffAction';

import type { ConversationTimelineItem, AIManagerConversationTranscript } from 'src/services/AIManagersService';

const OUTBOUND_LABELS: Record<string, string> = {
    draft: 'Черновик · не отправлен',
    approved: 'Подтверждено · не отправлено',
    queued: 'В очереди на отправку',
    sending: 'Отправляется',
    sent: 'Отправлено',
    skipped: 'Отправка пропущена',
    failed: 'Не отправлено · ошибка',
    canceled: 'Отправка отменена',
};

function statusLabel(item: ConversationTimelineItem) {
    if (item.kind === 'outbound') return OUTBOUND_LABELS[item.status || ''] || 'Статус отправки неизвестен';
    return '';
}

function day(value: string) {
    return new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(value));
}

function TimelineMessage({ item }: { item: ConversationTimelineItem }) {
    const isLead = item.kind === 'lead';
    const isSystem = item.role === 'system';
    const isAssistant = item.role === 'assistant';
    return (
        <Box
            component="article"
            aria-label={
                isLead
                    ? 'Исходный запрос'
                    : isSystem
                    ? 'Событие диалога'
                    : isAssistant
                    ? 'Ответ ИИ'
                    : 'Сообщение клиента'
            }
            sx={{
                px: isSystem ? 0 : 1.5,
                py: isSystem ? 0.5 : 1.25,
                ml: isAssistant ? { xs: 2, sm: 4 } : 0,
                mr: !isAssistant && !isLead && !isSystem ? { xs: 2, sm: 4 } : 0,
                borderRadius: 2,
                border: isLead ? '1px solid var(--lm-border)' : 0,
                bgcolor: isAssistant ? 'var(--lm-canvas)' : 'transparent',
                overflowWrap: 'anywhere',
            }}
        >
            <Box
                display="flex"
                alignItems="baseline"
                gap={1}
                justifyContent="space-between"
                mb={0.5}
            >
                <Typography
                    variant="caption"
                    fontWeight={700}
                    color="secondary.main"
                >
                    {isLead
                        ? 'Исходный запрос'
                        : isSystem
                        ? 'Событие'
                        : isAssistant
                        ? item.kind === 'message'
                            ? 'Подготовленный ответ ИИ'
                            : 'ИИ-менеджер'
                        : 'Клиент'}
                </Typography>
                <Typography
                    component="time"
                    dateTime={item.createdAt}
                    variant="caption"
                    color="secondary.main"
                    title={new Date(item.createdAt).toLocaleString('ru-RU')}
                    sx={{ flexShrink: 0 }}
                >
                    {new Date(item.createdAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                </Typography>
            </Box>
            <Typography
                variant="body2"
                color={isSystem ? 'secondary.main' : 'text.primary'}
                sx={{ whiteSpace: 'pre-wrap' }}
            >
                {item.content || 'Текст сообщения отсутствует'}
            </Typography>
            {statusLabel(item) && (
                <Typography
                    variant="caption"
                    component="p"
                    mt={1}
                    color={item.status === 'failed' ? 'var(--lm-danger-ink)' : 'secondary.main'}
                >
                    {statusLabel(item)}
                </Typography>
            )}
        </Box>
    );
}

export default function ConversationDetailsPanel({
    managerId,
    conversationId,
    onClose,
}: {
    managerId: string;
    conversationId: number;
    onClose: () => void;
}) {
    const client = useQueryClient();
    const { data, isLoading, isError, isFetching, refetch, error } = useQuery(
        ['manager-conversation', managerId, conversationId],
        () => AIManagersService.getConversationTranscript(managerId, conversationId),
        { staleTime: 0, cacheTime: 0, retry: false }
    );
    const status = (error as { response?: { status?: number } })?.response?.status;
    const unavailable = status === 401 || status === 403 || status === 404;
    const transcript = unavailable ? undefined : data;

    return (
        <Drawer
            anchor="right"
            open
            onClose={onClose}
            PaperProps={{ role: 'dialog', 'aria-modal': true, 'aria-labelledby': 'conversation-details-title' }}
            sx={{
                '& .MuiDrawer-paper': {
                    width: { xs: '100%', sm: 520 },
                    maxWidth: '100vw',
                    height: '100dvh',
                    bgcolor: 'var(--lm-surface)',
                    color: 'var(--lm-text)',
                    backgroundImage: 'none',
                },
            }}
        >
            <Box
                sx={{
                    p: 2,
                    borderBottom: '1px solid var(--lm-border)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexShrink: 0,
                }}
            >
                <Box
                    flex={1}
                    minWidth={0}
                >
                    <Typography
                        component="h2"
                        variant="h4"
                        id="conversation-details-title"
                        sx={{ overflowWrap: 'anywhere' }}
                    >
                        {transcript?.conversation.sourceAuthor || 'Диалог'}
                    </Typography>
                    {transcript && (
                        <Typography
                            variant="caption"
                            color="secondary.main"
                            component="p"
                            sx={{ overflowWrap: 'anywhere' }}
                        >
                            {[transcript.conversation.sourceChatTitle, transcript.conversation.salesStageTitle]
                                .filter(Boolean)
                                .join(' · ')}
                        </Typography>
                    )}
                </Box>
                <IconButton
                    aria-label="Обновить переписку"
                    disabled={isFetching}
                    onClick={() => {
                        void refetch();
                    }}
                >
                    <IconRefresh size={20} />
                </IconButton>
                <IconButton
                    autoFocus
                    aria-label="Закрыть диалог"
                    onClick={onClose}
                >
                    <IconX size={20} />
                </IconButton>
            </Box>
            <Box
                tabIndex={0}
                aria-label="Переписка"
                sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: 2, pb: 'max(24px, env(safe-area-inset-bottom))' }}
            >
                {isLoading && (
                    <Box
                        role="status"
                        display="flex"
                        alignItems="center"
                        gap={1}
                        py={2}
                    >
                        <CircularProgress size={18} />
                        <Typography variant="body2">Загружаем переписку…</Typography>
                    </Box>
                )}
                {isError && (
                    <Box
                        role="alert"
                        mb={2}
                    >
                        <Typography variant="body2">
                            {status === 401
                                ? 'Войдите снова, чтобы открыть диалог.'
                                : unavailable
                                ? 'Диалог недоступен этому менеджеру или удалён.'
                                : 'Не удалось загрузить переписку.'}
                        </Typography>
                        {transcript && (
                            <Typography
                                variant="body2"
                                color="secondary.main"
                            >
                                Показаны ранее загруженные сведения и переписка.
                            </Typography>
                        )}
                        {!unavailable && (
                            <Button
                                disabled={isFetching}
                                onClick={() => {
                                    void refetch();
                                }}
                            >
                                Повторить
                            </Button>
                        )}
                    </Box>
                )}
                {transcript?.context && <ConversationContextSummary context={transcript.context} />}
                {!isLoading && !isError && data?.timeline.length === 0 && (
                    <Typography
                        variant="body2"
                        color="secondary.main"
                        py={2}
                    >
                        История сообщений пока пуста.
                    </Typography>
                )}
                {!unavailable &&
                    data?.timeline.map((item, index) => (
                        <Stack
                            key={item.id}
                            spacing={1}
                            mb={1.5}
                        >
                            {(index === 0 || day(item.createdAt) !== day(data.timeline[index - 1].createdAt)) && (
                                <Typography
                                    variant="caption"
                                    color="secondary.main"
                                    textAlign="center"
                                    py={1}
                                    component="p"
                                >
                                    {day(item.createdAt)}
                                </Typography>
                            )}
                            <TimelineMessage item={item} />
                        </Stack>
                    ))}
            </Box>
            {transcript && (
                <ConversationHandoffAction
                    managerId={managerId}
                    conversation={transcript.conversation}
                    disabled={isFetching || isError}
                    hasSending={transcript.timeline.some(
                        (item) => item.kind === 'outbound' && item.status === 'sending'
                    )}
                    onTransferred={(result) => {
                        const key = ['manager-conversation', managerId, conversationId];
                        const previous = client.getQueryData<AIManagerConversationTranscript>(key);
                        if (previous) client.setQueryData(key, { ...previous, ...result });
                        void refetch();
                        EventService.dispatch('AI_MANAGERS_UPDATED');
                    }}
                    onChecked={(result) => {
                        client.setQueryData(['manager-conversation', managerId, conversationId], result);
                    }}
                    onUnavailable={() => {
                        void refetch();
                    }}
                />
            )}
        </Drawer>
    );
}
